import type { NonNullRef } from '@lemonade-hq/cdk';
import { ma, va } from '@lemonade-hq/chat-engine';
import type { GenericRecordData } from '@lemonade-hq/maschema-schema';
import type { I18nContextValue } from '@lemonade-hq/nuiza';
import { useI18nContext, useInfraContext } from '@lemonade-hq/nuiza';
import { devWarn, getRandomId, wrap } from '@lemonade-hq/ts-helpers';
import { useUniclientDialogContext } from '@lemonade-hq/uniclient-app';
import type { UniclientTrackingContextValue, WizardDialogProps } from '@lemonade-hq/uniclient-app';
import type {
  UniclientAction,
  UniclientDialogData,
  UniclientDynamicDialog,
  UniclientDynamicDialogProps,
  UniclientOpenDialogAction,
  UniclientOpenDynamicDialogWithDataOverrides,
} from '@lemonade-hq/uniclient-chat';
import {
  asyncRequest as asyncRequestAction,
  buildEdgeServiceUrl,
  closeDialog as closeDialogAction,
  compose as composeAction,
  content,
  EdgeService,
  form,
  input,
  next as nextAction,
  openToast,
  row,
  saveDialogValues,
} from '@lemonade-hq/uniclient-chat';
import { nativeApp } from '@lemonade-hq/uniclient-mobile-bridge';
import { useModal, useTheme } from '@lemonade-hq/uniclient-theme';
import deepmerge from 'deepmerge';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import customerSupportSvg from '../assets/customer-support.svg';
import { WizardDialog } from '../elements/WizardDialog';

const getHistoryEditDialogData = (s: I18nContextValue['s']): UniclientOpenDialogAction['dialog'] => {
  return {
    name: 'editHistory',
    content: {
      title: s('chat.history.edit_dialog.title'),
      component: content({
        items: [
          content.item({
            paragraph: s('chat.history.edit_dialog.content'),
          }),
        ],
      }),
      action: nextAction({
        primary: false,
        content: s('chat.history.edit_dialog.action'),
      }),
      secondaryAction: closeDialogAction({
        content: s('chat.history.edit_dialog.secondary_action'),
      }),
    },
  };
};

const getStartOverDialogData = (s: I18nContextValue['s']): UniclientOpenDialogAction['dialog'] => {
  return {
    name: 'startOver',
    content: {
      title: s('chat.start_over_dialog.title'),
      component: content({
        items: [
          content.item({
            paragraph: s('chat.start_over_dialog.content'),
          }),
        ],
      }),
      action: nextAction({
        primary: false,
        content: s('chat.start_over_dialog.action'),
      }),
      secondaryAction: closeDialogAction({
        content: s('chat.start_over_dialog.secondary_action'),
      }),
    },
  };
};

export const getAskUsAnythingDialogData = (
  s: I18nContextValue['s'],
  baseUrl: string,
  getThemeImageUrl: (imageUrl: string) => string,
): UniclientOpenDialogAction['dialog'] => {
  return {
    name: 'askUsAnything',
    content: {
      thumbnail: {
        src: getThemeImageUrl(customerSupportSvg),
        alt: s('chat.header.modal.ask_us_anything.thumbnail.alt'),
        type: 'image',
      },
      title: s('chat.header.modal.ask_us_anything.title'),
      subtitle: s('chat.header.modal.ask_us_anything.subtitle'),
      alignComponentLeft: true,
      component: form({
        content: [
          row({
            elements: [
              input.text({
                attribute: 'fullName',
                placeholder: s('chat.header.modal.ask_us_anything.full_name.placeholder'),
              }),
            ],
          }),
          row({
            elements: [
              input.email({
                attribute: 'email',
                placeholder: s('chat.header.modal.ask_us_anything.email.placeholder'),
              }),
            ],
          }),
          row({
            elements: [
              input.textArea({
                maxLines: 3,
                attribute: 'message',
                placeholder: s('chat.header.modal.ask_us_anything.message.placeholder'),
              }),
            ],
          }),
        ],
      }),
      action: composeAction({
        content: s('chat.header.modal.ask_us_anything.cta'),
        primary: false,
        actions: [
          asyncRequestAction({
            url: `${baseUrl}/_mrkt/contact_us`,
            method: 'POST',
            payloadMap: {
              name: '{fullName}',
              email: '{email}',
              question: '{message}',
            },
            headers: {
              'Content-Type': 'application/json',
            },
            successMessage: s('chat.header.modal.ask_us_anything.success'),
            errorMessage: s('chat.header.modal.ask_us_anything.error'),
          }),
          closeDialogAction(),
        ],
      }),
    },
    data: {
      schema: ma.record({
        fullName: ma.string([va.personNameChars()]),
        email: ma.string([va.email({ error: s('chat.header.modal.ask_us_anything.email.error') }), va.emailChars()]),
        message: ma.string([
          va.chars({ chars: 2, limit: 'min' }, { error: s('chat.header.modal.ask_us_anything.message.error') }),
        ]),
      }),
    },
  };
};

export const getSurveyDialogData = (
  s: I18nContextValue['s'],
  initialRating: number,
  scoreAttribute: string,
  commentAttribute: string,
): UniclientOpenDialogAction['dialog'] => ({
  name: 'survey',
  content: {
    title: s('chat.survey.title'),
    component: form({
      content: [
        row({ elements: [input.rating({ attribute: 'score' })], marginBottomSize: 'xl' }),
        row({
          elements: [
            input.textArea({
              attribute: 'comment',
              placeholder: s('chat.survey.comment.placeholder'),
            }),
          ],
        }),
      ],
    }),
    action: composeAction({
      content: s('chat.survey.action'),
      primary: false,
      actions: [
        saveDialogValues({
          data: [
            { targetAttribute: scoreAttribute, dialogAttribute: 'score' },
            { targetAttribute: commentAttribute, dialogAttribute: 'comment' },
          ],
        }),
        closeDialogAction(),
        openToast({
          title: s('chat.survey.success_toast.title'),
          toastType: 'success',
        }),
      ],
    }),
  },
  data: {
    initialValues: {
      score: initialRating,
      comment: '',
    },
    schema: ma.record({
      score: ma.number(),
      comment: ma.optional.string(),
    }),
  },
});

export const exitConfirmationDialog = (s: I18nContextValue['s']): UniclientOpenDialogAction['dialog'] => ({
  name: 'exitConfirmation',
  content: {
    title: s('chat.header.modal.exit.title'),
    subtitle: s('chat.header.modal.exit.subtitle'),
    action: nextAction({
      primary: false,
      content: s('chat.header.modal.exit.accept_action'),
    }),
  },
});

interface UseWizardDialogsProps {
  readonly currentStepIndex: number;
  readonly trackEvent: UniclientTrackingContextValue['trackEvent'];
  readonly onActionRef: NonNullRef<(action?: UniclientAction) => Promise<unknown>>;
  readonly persistedAnswers: GenericRecordData;
  readonly currentStepDialogs?: Record<string, UniclientDynamicDialog>;
}

type OpenDialogOptions = Pick<WizardDialogProps, 'actionsOverride' | 'enableScroll' | 'showCloseButtonForMobile'> & {
  readonly data?: UniclientDynamicDialogProps['data'];
};

interface UseWizardDialogs {
  readonly openDialog: (
    dialog: UniclientOpenDialogAction['dialog'] | UniclientOpenDynamicDialogWithDataOverrides | string,
    options?: OpenDialogOptions,
  ) => void;
  readonly openEditHistoryDialog: (stepName: string, callback: () => void) => void;
  readonly openStartOverDialog: (callback: () => void) => void;
  readonly openAskUsAnythingDialog: () => void;
  readonly openSurveyDialog: (initialRating: number, scoreAttribute: string, commentAttribute: string) => void;
  readonly closeDialog: () => void;
}

export function useWizardDialogs({
  currentStepIndex,
  trackEvent,
  onActionRef,
  persistedAnswers,
  currentStepDialogs,
}: UseWizardDialogsProps): UseWizardDialogs {
  const { isOpen: isModalOpen, open: openModal, close: closeModal } = useModal();
  const hasSubmittedRef = useRef(false);
  const { s } = useI18nContext();
  const dialogHistoryEditData = useMemo(() => getHistoryEditDialogData(s), [s]);
  const dialogDataStartOver = useMemo(() => getStartOverDialogData(s), [s]);
  const { env, envId } = useInfraContext();
  const { getThemeImageUrl } = useTheme();
  const monolithUrl = useMemo(
    () => window.lemonade?.monolithUrl ?? buildEdgeServiceUrl(EdgeService.Monolith, env, envId),
    [env, envId],
  );
  const dialogDataAskUsAnything = useMemo(
    () => getAskUsAnythingDialogData(s, monolithUrl, getThemeImageUrl),
    [s, monolithUrl, getThemeImageUrl],
  );
  const { setProps: setDialogProps } = useUniclientDialogContext();
  const [currentlyOpenDialog, setCurrentlyOpenDialog] = useState<
    UniclientOpenDynamicDialogWithDataOverrides | string | null
  >(null);

  const getDialogProps = useCallback(
    (dialog: UniclientDynamicDialogProps | UniclientOpenDynamicDialogWithDataOverrides | string) => {
      let actualProps: UniclientDynamicDialogProps | null = null;

      const isDialogString = typeof dialog === 'string';

      const dialogName = isDialogString ? dialog : dialog.name;
      const dialogPropsFromStep = currentStepDialogs?.[dialogName];

      if (dialogPropsFromStep != null) {
        const dialogDataOverride = (isDialogString ? {} : dialog.data) as UniclientDialogData;
        actualProps = {
          ...deepmerge<UniclientDynamicDialogProps>(dialogPropsFromStep, { data: dialogDataOverride }),
          name: dialogName,
          dynamicHeight: dialogPropsFromStep.dynamicHeight,
        };
        setCurrentlyOpenDialog(dialog);
      } else if (isDialogString) {
        devWarn(`Dialog ${dialog} not found in current step dialogs`);
        return;
      } else if ('content' in dialog) {
        actualProps = dialog;
      }

      return actualProps;
    },
    [currentStepDialogs],
  );

  const updateDialogProps = useCallback(
    (
      dialog: UniclientDynamicDialogProps | UniclientOpenDynamicDialogWithDataOverrides | string,
      options: OpenDialogOptions = {},
    ) => {
      const actualProps = getDialogProps(dialog);
      if (actualProps == null) return;

      const id = getRandomId();

      setDialogProps({
        name: actualProps.name,
        fullscreen: actualProps.fullscreen,
        dismissible: actualProps.dismissible,
        disclaimer: actualProps.disclaimer,
        steps: wrap(actualProps.content),
        onActionRef,
        answersSchema: actualProps.data?.schema ?? ma.record({}),
        persistedAnswers: { id, ...actualProps.data?.initialValues, ...persistedAnswers },
        resyncingActions: actualProps.data?.resyncActions,
        resyncingDebounce: actualProps.data?.resyncingDebounce,
        loading: actualProps.loading,
        onSubmit: answers => {
          hasSubmittedRef.current = true;
          trackEvent('dialog.submitted', { ...actualProps.analytics, dialogName: actualProps.name, answers });
        },
        dynamicHeight: actualProps.dynamicHeight,
        ...options,
      });

      return actualProps;
    },
    [setDialogProps, onActionRef, persistedAnswers, getDialogProps, trackEvent],
  );
  const openDialog = useCallback(
    (
      dialog: UniclientDynamicDialogProps | UniclientOpenDynamicDialogWithDataOverrides | string,
      options?: OpenDialogOptions,
    ) => {
      hasSubmittedRef.current = false;

      const actualProps = updateDialogProps(dialog, options);
      if (actualProps == null) return;

      openModal({
        fullscreen: actualProps.fullscreen,
        dismissible: actualProps.dismissible,
        disclaimer: actualProps.disclaimer,
        dynamicHeight: actualProps.dynamicHeight,
        size: actualProps.size,
        showCloseButtonForMobile: options?.showCloseButtonForMobile,
        enableScroll: options?.enableScroll,
        component: WizardDialog,
        props: {},
        onOpenChange: state => {
          if (state || hasSubmittedRef.current) return;

          setCurrentlyOpenDialog(null);

          trackEvent('dialog.dismissed', {
            ...actualProps.analytics,
            dialogName: actualProps.name,
          });
        },
      });

      trackEvent('dialog.viewed', {
        ...actualProps.analytics,
        dialogName: actualProps.name,
      });
    },
    [openModal, trackEvent, updateDialogProps],
  );

  const openSurveyDialog = useCallback(
    (initialRating: number, scoreAttribute: string, commentAttribute: string) => {
      openDialog(getSurveyDialogData(s, initialRating, scoreAttribute, commentAttribute), {
        showCloseButtonForMobile: true,
        enableScroll: false,
      });
    },
    [openDialog, s],
  );

  const openEditHistoryDialog = useCallback(
    (stepName: string, callback: () => void) => {
      openDialog(
        {
          ...dialogHistoryEditData,
          analytics: {
            editedStep: stepName,
          },
        },
        {
          actionsOverride: {
            primary: callback,
          },
        },
      );
    },
    [dialogHistoryEditData, openDialog],
  );

  const openStartOverDialog = useCallback(
    (callback: () => void) => {
      openDialog(
        {
          ...dialogDataStartOver,
        },
        {
          actionsOverride: {
            primary: callback,
          },
        },
      );
    },
    [dialogDataStartOver, openDialog],
  );

  const openAskUsAnythingDialog = useCallback(() => {
    openDialog({
      ...dialogDataAskUsAnything,
    });
  }, [openDialog, dialogDataAskUsAnything]);

  useEffect(() => {
    if (isModalOpen) closeModal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStepIndex]);

  useEffect(() => {
    const dialog = exitConfirmationDialog(s);
    const cleanup = nativeApp.listenForMessage('backButton', () => {
      openDialog(dialog, {
        actionsOverride: {
          primary: () => {
            void nativeApp.dismiss();
          },
        },
      });
    });

    return () => {
      cleanup();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onActionRef]);

  useEffect(() => {
    if (currentlyOpenDialog != null) {
      updateDialogProps(currentlyOpenDialog);
    }
    // updateDialogProps updates every time currentStepDialogs changes
  }, [currentlyOpenDialog, updateDialogProps]);

  return {
    openDialog,
    openEditHistoryDialog,
    openAskUsAnythingDialog,
    openStartOverDialog,
    closeDialog: closeModal,
    openSurveyDialog,
  };
}
